import React from 'react'
import { MenuItem } from '@material-ui/core'
import i18n from 'common/util/internationalization'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'

export type BookStructure = 'TOP_LEVEL' | 'ENTIRE_BOOK' | 'NONE' | 'AI_ASSIST'
type BookStructureItem = { value: BookStructure; label: string; svgPath: JSX.Element }

/**
 * Recommended to memoize the result when using this
 * Ex: useMemo(() => timezoneMenuItems(), []))
 */
export const linkedBookStructureMenuItems = (): JSX.Element[] => {
    const AIItems: BookStructureItem[] =
        transientStorageManager.platform !== 'boardeffect'
            ? [
                  {
                      value: 'AI_ASSIST',
                      label: i18n.t('BOOK_STRUCTURE_ITEMS.AI_ASSIST'),
                      svgPath: (
                          <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg">
                              <path
                                  fill-rule="evenodd"
                                  clip-rule="evenodd"
                                  d="M17.75 9L19 6.5L21.5 5.25L19 4L17.75 1.5L16.5 4L14 5.25L16.5 6.5L17.75 9ZM11.585 9.415L9 4.25L6.415 9.415L1.25 12L6.415 14.585L9 19.75L11.585 14.585L16.75 12L11.585 9.415ZM16.5 17.5L17.75 15L19 17.5L21.5 18.75L19 20L17.75 22.5L16.5 20L14 18.75L16.5 17.5Z"
                                  fill="url(#paint0_linear_917_31569)"
                              />
                              <defs>
                                  <linearGradient
                                      id="paint0_linear_917_31569"
                                      x1="2.18103"
                                      y1="8.5875"
                                      x2="21.0892"
                                      y2="23.0375"
                                      gradientUnits="userSpaceOnUse">
                                      <stop stop-color="#D3222A" />
                                      <stop offset="0.64" stop-color="#C247FA" />
                                      <stop offset="1" stop-color="#4D1DD5" />
                                  </linearGradient>
                              </defs>
                          </svg>
                      )
                  }
              ]
            : []
    const items: BookStructureItem[] = [
        ...AIItems,
        {
            value: 'NONE',
            label: i18n.t('BOOK_STRUCTURE_ITEMS.NONE'),
            svgPath: (
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0.75 2C0.335786 2 0 2.33579 0 2.75V19.25C0 19.6642 0.335786 20 0.75 20H9C9.59674 20 10.169 20.2371 10.591 20.659C11.0129 21.081 11.25 21.6533 11.25 22.25C11.25 22.6642 11.5858 23 12 23C12.4142 23 12.75 22.6642 12.75 22.25C12.75 21.6533 12.9871 21.081 13.409 20.659C13.831 20.2371 14.4033 20 15 20H23.25C23.6642 20 24 19.6642 24 19.25V2.75C24 2.33579 23.6642 2 23.25 2H15C14.0054 2 13.0516 2.39509 12.3484 3.09835C12.2221 3.22456 12.1059 3.35884 12 3.49998C11.8941 3.35884 11.7779 3.22456 11.6517 3.09835C10.9484 2.39509 9.99456 2 9 2H0.75ZM12.75 5.75V19.25C13.3953 18.766 14.1839 18.5 15 18.5H22.5V3.5H15C14.4033 3.5 13.831 3.73705 13.409 4.15901C12.9871 4.58097 12.75 5.15326 12.75 5.75ZM11.25 5.75C11.25 5.15326 11.0129 4.58097 10.591 4.15901C10.169 3.73705 9.59674 3.5 9 3.5H1.5V18.5H9C9.81606 18.5 10.6047 18.766 11.25 19.25V5.75Z"
                        fill="#1E1E1E"
                    />
                </svg>
            )
        },
        {
            value: 'TOP_LEVEL',
            label: i18n.t('BOOK_STRUCTURE_ITEMS.TOP_LEVEL'),
            svgPath: (
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0.91 0H16.03C17.2413 0 18.22 0.996657 18.22 2.21739V3.05505H19.3731C19.9799 3.05505 20.5331 3.52202 20.5331 4.19049V7.38931V8.57663V11.904V13.0913V16.4187V17.606V20.444C20.5331 21.1114 19.9802 21.5794 19.3731 21.5794H18.22V21.7826C18.22 23.0024 17.241 24 16.03 24H0.91C0.403763 24 0 23.5863 0 23.087V0.913043C0 0.41196 0.40427 0 0.91 0ZM18.22 4.05505V7.38931H19.5331V4.19049C19.5331 4.11518 19.4614 4.05505 19.3731 4.05505H18.22ZM18.22 8.57663V11.904H19.5331V8.57663H18.22ZM18.22 13.0913V16.4187H19.5331V13.0913H18.22ZM18.22 17.606V20.5794H19.3731C19.4614 20.5794 19.5331 20.5187 19.5331 20.444V17.606H18.22ZM1.5 1.5V22.5H16.03C16.4062 22.5 16.72 22.1802 16.72 21.7826V2.21739C16.72 1.81903 16.4067 1.5 16.03 1.5H1.5ZM13.27 4.84974C13.8871 4.84974 14.39 5.35265 14.39 5.96974V8.52974C14.39 9.14684 13.8871 9.64974 13.27 9.64974H4.95C4.3329 9.64974 3.83 9.14684 3.83 8.52974V5.96974C3.83 5.35265 4.3329 4.84974 4.95 4.84974H13.27ZM13.27 5.84974H4.95C4.88519 5.84974 4.83 5.90493 4.83 5.96974V8.52974C4.83 8.59456 4.88519 8.64974 4.95 8.64974H13.27C13.3348 8.64974 13.39 8.59456 13.39 8.52974V5.96974C13.39 5.90493 13.3348 5.84974 13.27 5.84974Z"
                        fill="#1E1E1E"
                    />
                </svg>
            )
        },
        {
            value: 'ENTIRE_BOOK',
            label: i18n.t('BOOK_STRUCTURE_ITEMS.ENTIRE_BOOK'),
            svgPath: (
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0.75 2.2124C0.335786 2.2124 0 2.54819 0 2.9624V19.4624C0 19.8766 0.335786 20.2124 0.75 20.2124H9C9.59674 20.2124 10.169 20.4495 10.591 20.8714C11.0129 21.2934 11.25 21.8657 11.25 22.4624C11.25 22.8766 11.5858 23.2124 12 23.2124C12.4142 23.2124 12.75 22.8766 12.75 22.4624C12.75 21.8657 12.9871 21.2934 13.409 20.8714C13.831 20.4495 14.4033 20.2124 15 20.2124H23.25C23.6642 20.2124 24 19.8766 24 19.4624V2.9624C24 2.54819 23.6642 2.2124 23.25 2.2124H15C14.0054 2.2124 13.0516 2.60749 12.3484 3.31075C12.2221 3.43696 12.1059 3.57124 12 3.71238C11.8941 3.57124 11.7779 3.43696 11.6517 3.31075C10.9484 2.60749 9.99456 2.2124 9 2.2124H0.75ZM9 18.7124C9.81606 18.7124 10.6047 18.9784 11.25 19.4624V5.9624C11.25 5.36566 11.0129 4.79337 10.591 4.37141C10.169 3.94946 9.59674 3.7124 9 3.7124H1.5V18.7124H9ZM12.75 19.4624V5.9624C12.75 5.36567 12.9871 4.79337 13.409 4.37141C13.831 3.94946 14.4033 3.7124 15 3.7124H22.5V18.7124H15C14.1839 18.7124 13.3953 18.9784 12.75 19.4624ZM3.75 5.9624C3.33579 5.9624 3 6.29819 3 6.7124C3 7.12662 3.33579 7.4624 3.75 7.4624H9C9.41421 7.4624 9.75 7.12662 9.75 6.7124C9.75 6.29819 9.41421 5.9624 9 5.9624H3.75ZM15 5.9624C14.5858 5.9624 14.25 6.29819 14.25 6.7124C14.25 7.12662 14.5858 7.4624 15 7.4624H20.25C20.6642 7.4624 21 7.12662 21 6.7124C21 6.29819 20.6642 5.9624 20.25 5.9624H15ZM3.75 8.9624C3.33579 8.9624 3 9.29819 3 9.7124C3 10.1266 3.33579 10.4624 3.75 10.4624H9C9.41421 10.4624 9.75 10.1266 9.75 9.7124C9.75 9.29819 9.41421 8.9624 9 8.9624H3.75ZM15 8.9624C14.5858 8.9624 14.25 9.29819 14.25 9.7124C14.25 10.1266 14.5858 10.4624 15 10.4624H20.25C20.6642 10.4624 21 10.1266 21 9.7124C21 9.29819 20.6642 8.9624 20.25 8.9624H15ZM3.75 11.9624C3.33579 11.9624 3 12.2982 3 12.7124C3 13.1266 3.33579 13.4624 3.75 13.4624H9C9.41421 13.4624 9.75 13.1266 9.75 12.7124C9.75 12.2982 9.41421 11.9624 9 11.9624H3.75ZM15 11.9624C14.5858 11.9624 14.25 12.2982 14.25 12.7124C14.25 13.1266 14.5858 13.4624 15 13.4624H20.25C20.6642 13.4624 21 13.1266 21 12.7124C21 12.2982 20.6642 11.9624 20.25 11.9624H15ZM3.75 14.9624C3.33579 14.9624 3 15.2982 3 15.7124C3 16.1266 3.33579 16.4624 3.75 16.4624H9C9.41421 16.4624 9.75 16.1266 9.75 15.7124C9.75 15.2982 9.41421 14.9624 9 14.9624H3.75ZM15 14.9624C14.5858 14.9624 14.25 15.2982 14.25 15.7124C14.25 16.1266 14.5858 16.4624 15 16.4624H20.25C20.6642 16.4624 21 16.1266 21 15.7124C21 15.2982 20.6642 14.9624 20.25 14.9624H15Z"
                        fill="#1E1E1E"
                    />
                </svg>
            )
        }
    ]
    if (transientStorageManager.platform === 'boardeffect') {
    }
    const menuItems = items.map((val, index) => {
        return (
            <MenuItem
                key={val?.value! + index}
                value={val?.value}
                data-testid={`LinkedBookStructureSelect_Option`}
                data-analytics={
                    val?.value === 'AI_ASSIST' ? 'MM-AI-PreGenerated-GeneratedByAi' : undefined
                }
                disabled={
                    val?.value === 'AI_ASSIST' &&
                    !transientStorageManager.book_summarization_enabled
                }
                style={{
                    pointerEvents:
                        !transientStorageManager.book_summarization_enabled &&
                        val?.value === 'AI_ASSIST'
                            ? 'none'
                            : 'auto'
                }}>
                {val?.value === 'AI_ASSIST' ? (
                    <AtlasTooltip
                        title={
                            !transientStorageManager.book_summarization_enabled
                                ? i18n.t('AI_NO_ACCESS_ERROR_MESSAGE') || 'Default Tooltip Message'
                                : ''
                        }
                        placement="top-start">
                        <div
                            style={{
                                ...MenuItemContent,
                                display: 'flex',
                                alignItems: 'center',
                                cursor: !transientStorageManager.book_summarization_enabled
                                    ? 'default'
                                    : 'pointer',
                                opacity: !transientStorageManager.book_summarization_enabled
                                    ? 0.5
                                    : 1,
                                pointerEvents: 'auto'
                            }}
                            onClick={(e) => {
                                if (!transientStorageManager.book_summarization_enabled) {
                                    e.preventDefault()
                                    e.stopPropagation()
                                }
                            }}>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg">
                                {val?.svgPath}
                            </svg>
                            <p style={MenuItemLabel}>{val?.label}</p>
                        </div>
                    </AtlasTooltip>
                ) : (
                    <div style={MenuItemContent}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg">
                            {val?.svgPath}
                        </svg>
                        <p style={MenuItemLabel}>{val?.label}</p>
                    </div>
                )}
            </MenuItem>
        )
    })

    return menuItems
}

export const DEFAULT_BOOK_STRUCTURE_ITEM = 'TOP_LEVEL'
const MenuItemContent = {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '6px'
}

const MenuItemLabel = {
    // height: '24px',
    textIndent: '8px',
    fontWeight: 400,
    margin: 'auto 0'
}
